const QUIZ_SEND_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAud6IACM/km0vgzo4wN9Z
afZ1evsBz2zS7zqIyXKQuuJ/AT32e+3RNs8x23lO54Lu7IAMGmNuGr+5SjfdeWZm
mx5hvvdHDmaSnItr8dgfet6VqG2dDN/4yBxxLQZJo4hViK6TlQh+wxFgI1WJcnua
qahbXjBIfqeEalHpl7p7LPoToowDIxz+VQbIb5lW8EY6koK9k1YqXeRtDTezbw3i
J0eeWMIseyihxAM/Mx29y/cThcUHCWngvtXloL7Qap7htspPmfdKzWrPPu4FJVG4
/G6A9OGhlSiwMqFiktdMDSnGivm7snuUwwP7wN+CDRAI0Bn73nYlXQ96lkrpRkI3
lwIDAQAB
-----END PUBLIC KEY-----`;

const QUIZ_SEND_ENCRYPTION_VERSION = "rsa-oaep-aes-gcm-v1";

export interface EncryptedQuizSendPayload {
  encrypted: true;
  version: typeof QUIZ_SEND_ENCRYPTION_VERSION;
  key: string;
  iv: string;
  payload: string;
}

const base64ToArrayBuffer = (value: string): ArrayBuffer => {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes.buffer;
};

const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = "";

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary);
};

const getPublicKeyDer = (publicKey: string): ArrayBuffer => {
  const base64 = publicKey
    .replace(/-----BEGIN PUBLIC KEY-----/g, "")
    .replace(/-----END PUBLIC KEY-----/g, "")
    .replace(/\s/g, "");

  return base64ToArrayBuffer(base64);
};

const importPublicKey = async (): Promise<CryptoKey> => {
  return await crypto.subtle.importKey(
    "spki",
    getPublicKeyDer(QUIZ_SEND_PUBLIC_KEY),
    {
      name: "RSA-OAEP",
      hash: "SHA-256"
    },
    false,
    ["encrypt"]
  );
};

export const encryptQuizSendPayload = async <T extends object>(
  payload: T
): Promise<EncryptedQuizSendPayload> => {
  if (!crypto?.subtle) {
    throw new Error("Quiz send encryption is not supported");
  }

  const publicKey = await importPublicKey();
  const aesKey = await crypto.subtle.generateKey(
    {
      name: "AES-GCM",
      length: 256
    },
    true,
    ["encrypt"]
  );
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encodedPayload = new TextEncoder().encode(JSON.stringify(payload));
  const encryptedData = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv
    },
    aesKey,
    encodedPayload
  );
  const rawAesKey = await crypto.subtle.exportKey("raw", aesKey);
  const encryptedKey = await crypto.subtle.encrypt(
    {
      name: "RSA-OAEP"
    },
    publicKey,
    rawAesKey
  );

  return {
    encrypted: true,
    version: QUIZ_SEND_ENCRYPTION_VERSION,
    key: bytesToBase64(new Uint8Array(encryptedKey)),
    iv: bytesToBase64(iv),
    payload: bytesToBase64(new Uint8Array(encryptedData))
  };
};
